/* eslint-disable camelcase */
/* eslint-disable no-console */
import { Channel } from '../stores/reducers/home';
import { ChannelAuthMethod } from '../stores/types/types';
import { APP } from '../stores/session-status';
import { getHeaders } from './utils';
import { doesEnvVarExist } from '../utils/utils';

interface GreenroomTokenResponse {
	token: string;
	app_signed_in_with: string;
	channelIds: number[];
}

export interface PresenterAllowed {
	auth_required: string;
    allowed: boolean;
}

export interface PresenterCreationReturn {
    token: string;
    app_signed_in_with: string;
    verified: boolean;
}

export interface CreatePlatformAccountResponse {
	status: number | null;
	success: boolean;
}

export const CheckAppsEmailIsAProducerFor = async (email: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/check-apps-email-is-producer-for`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
			}),
		});
		if (res.ok) return res.json();
		return { message: 'Response Error' };
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

export const ProducerLogin = async (
	email: string,
	password: string,
	app: string,
	storedTwoFactorEvents?: string,
) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/producer/login/`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
				password,
				app,
				storedTwoFactorEvents,
			}),
		});
		if (res.ok) return await res.json();
		const errorResponse = await res.json();
		return errorResponse;
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

export const PresenterLogin = async (email: string, password: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/presenter/login/`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
				password,
			}),
		});
		if (res.ok) return res.json();
		const errorResponse = await res.json();
		return errorResponse;
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

export const CreateAccount = async (
	firstName: string,
	lastName: string,
	email: string,
	password: string,
	is_producer: boolean,
	verification_code: string | undefined,
) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/account`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				first_name: firstName,
				last_name: lastName,
				email,
				password,
				is_producer,
				verification_code,
			}),
		});

		if (res.ok) {
			return res.json();
		} if (res.status === 400) {
			return { message: 'exists' };
		}
		return { message: 'Response Error' };
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

export const VerifyAccount = async (code: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/verify`, {
			method: 'PUT',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				code,
			}),
		});
		if (res.ok) return res.json();
		return { message: 'Response Error' };
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

interface User {
	first_name?: string;
	last_name?: string;
	avatar?: string;
	color?: string;
	title?: string;
	location?: string;
	bg_image?: string
}

export const UpdateProfile = async (
	gr_user: number,
	app_signed_in_with: string,
	columns: User,
	token: string,
	showrooms_access_token?: string,
) => {
	const headers = {
		Accept: 'application/json',
		'Content-Type': 'application/json',
	} as any;

	if (app_signed_in_with === 'showrooms') {
		headers['showrooms-token'] = showrooms_access_token;
	}

	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/profile`, {
			method: 'PUT',
			headers,
			body: JSON.stringify({
				token,
				app_signed_in_with,
				gr_user,
				columns,
			}),
		});
		if (res.ok) {
			const response = await res.json();
			return response.token;
		}
		return { message: 'Response Error' };
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

export const UpdateBgImage = async (token: string, image: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/profile/bg-image`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ token, image }),
		});
		if (res.ok) {
			const response = await res.json();
			return response.token;
		}
		return { message: 'Response Error' };
	} catch (e) {
		console.error(e);
		return { message: 'Connection Error' };
	}
};

export const ForgotPasswordEvents = async (email: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/forgot-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email, is_greenroom: true }),
		});
		if (res.ok) return true;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Forgot Password Failure');
};

export const ForgotPasswordGreenroom = async (email: string, isProducer: boolean) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/forgot-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email, is_producer: isProducer }),
		});
		if (res.ok) return true;
		if (res.status === 404) return 404;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Forgot Password Failure');
};

export const UpdatePasswordEvents = async (password: string, code: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/update-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ password, code }),
		});
		if (res.ok) return true;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Password Reset Failure');
};

export const UpdatePasswordGreenroom = async (password: string, code: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/update-password`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ password, code }),
		});
		if (res.ok) return true;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Password Reset Failure');
};

export const ResendVerificationEmail = async (email: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/resend-verification`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ email }),
		});
		if (res.ok) return true;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Resend Verification Email Failure');
};

export const RefreshToken = async (token: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/refresh-token`, {
			method: 'GET',
			headers: getHeaders(token) as any,
		});
		if (res.ok) return res.json();
	} catch (e) {
		console.error(e);
	}
	throw new Error('Unable to refresh token');
};

export const GetCurrentProfile = async (
	token: string,
	appSignedInWith: string,
	showroomsToken?: string,
) => {
	const headers = getHeaders(token) as any;
	if (appSignedInWith === APP.SHOWROOMS) {
		headers['showrooms-token'] = showroomsToken;
	}

	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/profile/${appSignedInWith}`, {
			method: 'GET',
			headers,
		});
		if (res.ok) return res.json();
	} catch (e) {
		console.error(e);
	}
	throw new Error('Unable to get current profile');
};

export const TempPhone = async (phone: string, temp_profile: string): Promise<any> => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/set-temp-phone`, {
			method: 'POST',
			headers: getHeaders() as any,
			body: JSON.stringify({
				temp_profile,
				phone,
			}),
		});
		if (res.ok) return res.json();
	} catch (e) {
		console.error(e);
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/set-temp-phone didn't work`);
};

export const ConfirmCode = async (
	temp_profile: string,
	code: string,
	request_storage: boolean,
): Promise<any> => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/confirm-code`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				temp_profile,
				code,
				request_storage,
			}),
		});
		if (res.status === 403) return { message: 'incorrect confirmation code given' };
		if (res.ok) return res.json();
	} catch (e) {
		console.error(e);
	}
	throw new Error(`${process.env.REACT_APP_API_HOST}/v2/greenroom/events/confirm-code error confirming code`);
};

export const UpdateChannel = async (token: string, channel: Channel) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/admin/channels/update`, {
			method: 'POST',
			headers: getHeaders(token) as any,
			body: JSON.stringify(channel),
		});
		if (res.ok) return channel;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Unable to update channel');
};

export const isBrandliveEmployee = async (token: string) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/is-brandlive-employee`, {
			method: 'GET',
			headers: getHeaders(token) as any,
		});
		if (res.ok) return res.json();
	} catch (e) {
		console.error(e);
	}
	throw new Error('Unable to check if brandlive user');
};

export const deleteAccountAPI = (token: string) => fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/user`, {
	method: 'DELETE',
	headers: getHeaders(token) as any,
}).then((result) => {
	if (result.ok) {
		return true;
	}
	return false;
});

export const AddLoginActivity = async (
	token: string,
	activityType: string,
	hostUrl: string,
	appSignedInWith: string | null,
) => {
	try {
		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/login-activity`, {
			method: 'POST',
			headers: getHeaders(token) as any,
			body: JSON.stringify({
				activity_type: activityType,
				host_url: hostUrl,
				app_signed_in_with: appSignedInWith,
			}),
		});
		if (res.ok) {
			return true;
		}
		return false;
	} catch (e) {
		console.error(e);
	}
	throw new Error('Unable to add login activity');
};

export const GetGreenroomToken = async (
	platformToken: string,
	meetingId?: number,
): Promise<GreenroomTokenResponse | null> => {
	try {
		if (!process.env.REACT_APP_API_HOST) {
			console.error('Please check environment.');

			return null;
		}

		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v2/greenroom/auth/token`, {
			method: 'PUT',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
				'X-IS-PRODUCER': 'false',
				'X-MEETING': meetingId ? meetingId.toString() : '',
			},
			body: JSON.stringify({
				platformToken,
			}),
		});

		if (res.ok) {
			const data: GreenroomTokenResponse = await res.json();

			return data;
		}

		throw new Error('Unable to get greenroom token');
	} catch (e) {
		console.error('GetGreenroomToken error', e);

		return null;
	}
};

export const CreatePlatformAccount = async (
	email: string,
	firstName?: string,
	lastName?: string,
	meetingHash?: string,
): Promise<CreatePlatformAccountResponse> => {
	let status = null;

	try {
		if (!process.env.REACT_APP_API_HOST) {
			throw new Error('Please check environment.');
		}

		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v3/greenroom/account`, {
			method: 'POST',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email,
				...(firstName && { firstName }),
				...(lastName && { lastName }),
				...(meetingHash && { meetingHash }),
			}),
		});

		status = res?.status || null;

		if (res?.ok) {
			// res.json() will return => Promise<PresenterCreationReturn>
			// Just worrying about the status since we get the token from Platform
			return {
				status,
				success: true,
			};
		}

		throw new Error('Unable to create platform account.');
	} catch (e) {
		console.error('CreatePlatformAccount error:', e);

		return {
			status,
			success: false,
		};
	}
};

interface UpdatePresenterAuthResponse {
    platformProfileId: number;
    channelId: number;
    authType: ChannelAuthMethod;
    email: string;
}

export const UpdatePresenterAuth = async (
	token: string,
	platformProfileId: number,
	channelId: number,
	authType: ChannelAuthMethod,
): Promise<UpdatePresenterAuthResponse | null> => {
	let status = null;

	try {
		if (!process.env.REACT_APP_API_HOST) {
			throw new Error('Please check environment.');
		}

		const res = await fetch(`${process.env.REACT_APP_API_HOST}/v3/greenroom/account/auth`, {
			method: 'PUT',
			headers: getHeaders(token),
			body: JSON.stringify({
				platformProfileId,
				channelId,
				authType,
			}),
		});

		if (res.ok) {
			return res.json();
		}

		status = res.status || null;

		throw new Error('Unable to update presenter auth type.');
	} catch (e) {
		console.error(`UpdatePresenterAuth: status ${status}, error:`, e);

		return null;
	}
};

export const GetPresenterAllowed = async (
	token: string,
	meetingHash: string,
): Promise<PresenterAllowed | null> => {
	try {
		const envVarExists = doesEnvVarExist('REACT_APP_API_HOST');

		if (!envVarExists) {
			return null;
		}

		const res = await fetch(
			`${process.env.REACT_APP_API_HOST}/v2/greenroom/meetings/${meetingHash}/authorized`,
			{
				method: 'GET',
				headers: getHeaders(token),
			},
		);

		if (res.ok) {
			return res.json();
		}

		console.error('GetPresenterAllowed: Unable to get presenter auth status.');

		return null;
	} catch (e) {
		console.error('GetPresenterAllowed: error', e);

		return null;
	}
};

export const GetInviteSsoDomains = async (token: string, meetingId: number): Promise<string[]> => {
	try {
		const envVarExists = doesEnvVarExist('REACT_APP_API_HOST');

		if (!envVarExists) {
			return [];
		}

		const res = await fetch(
			`${process.env.REACT_APP_API_HOST}/v2/greenroom/auth/domains/${meetingId}`,
			{
				method: 'GET',
				headers: getHeaders(token),
			},
		);

		if (res.ok) {
			return res.json();
		}

		console.error('getSSODomains: Unable to get SSO domains.');

		return [];
	} catch (e) {
		console.error('getSSODomains: error', e);

		return [];
	}
};
